import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { oauthSessionsApi } from '../api/index.js';
import { useAuth } from '../hooks/useAuth.js';
import type { OAuthSessionDto } from '../types.js';

type StatusFilter = 'all' | 'active' | 'expired' | 'revoked';

function statusBadge(status: OAuthSessionDto['status']) {
  const cls = status === 'active' ? 'badge-green' : status === 'expired' ? 'badge-gray' : 'badge-red';
  return <span className={`badge ${cls}`}>{status}</span>;
}

function fmt(ms: number | null) {
  if (ms === null) return '—';
  return new Date(ms).toLocaleString();
}

export function OAuthSessionsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['oauth-sessions'],
    queryFn: oauthSessionsApi.list,
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => oauthSessionsApi.revoke(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['oauth-sessions'] }),
  });

  const all = data?.sessions ?? [];
  const sessions = filter === 'all' ? all : all.filter((s) => s.status === filter);

  const counts = {
    active: all.filter((s) => s.status === 'active').length,
    expired: all.filter((s) => s.status === 'expired').length,
    revoked: all.filter((s) => s.status === 'revoked').length,
  };

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '.5rem' }}>OAuth Sessions</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '.875rem', marginBottom: '1.5rem' }}>
        {user?.role === 'admin'
          ? 'All OAuth sessions across users. You can revoke any session.'
          : 'OAuth sessions granted to external clients via the authorization flow. Revoke any you no longer recognize.'}
      </p>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1.5rem' }}>
        {(['all', 'active', 'expired', 'revoked'] as StatusFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={filter === f ? 'btn btn-primary' : 'btn btn-outline'}
            style={{ fontSize: '.8rem', padding: '.3rem .75rem', textTransform: 'capitalize' }}
          >
            {f}
            {f !== 'all' && (
              <span style={{ marginLeft: '.35rem', opacity: 0.7 }}>({counts[f]})</span>
            )}
            {f === 'all' && (
              <span style={{ marginLeft: '.35rem', opacity: 0.7 }}>({all.length})</span>
            )}
          </button>
        ))}
      </div>

      <div className="card">
        {isLoading ? (
          <p>Loading…</p>
        ) : sessions.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>
            {filter === 'all' ? 'No OAuth sessions yet.' : `No ${filter} sessions.`}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Scopes</th>
                  {user?.role === 'admin' && <th>User ID</th>}
                  <th>Resource</th>
                  <th>Authorized</th>
                  <th>Access Expires</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{s.clientName}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>
                        {s.clientId.slice(0, 12)}…
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '.25rem', flexWrap: 'wrap' }}>
                        {s.scopes.map((sc) => (
                          <code key={sc} style={{ fontSize: '.75rem', background: 'var(--color-bg)', padding: '.1rem .35rem', borderRadius: '4px', border: '1px solid var(--color-border)' }}>
                            {sc}
                          </code>
                        ))}
                      </div>
                    </td>
                    {user?.role === 'admin' && (
                      <td style={{ fontSize: '.8rem', color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>
                        {s.userId.slice(0, 10)}…
                      </td>
                    )}
                    <td style={{ fontSize: '.8rem', color: 'var(--color-text-muted)', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.resource ?? '—'}
                    </td>
                    <td style={{ fontSize: '.8rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                      {fmt(s.createdAt)}
                    </td>
                    <td style={{ fontSize: '.8rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                      {fmt(s.accessExpiresAt)}
                    </td>
                    <td>{statusBadge(s.status)}</td>
                    <td>
                      {s.status !== 'revoked' && (
                        <button
                          className="btn btn-danger"
                          style={{ fontSize: '.75rem', padding: '.25rem .5rem', whiteSpace: 'nowrap' }}
                          disabled={revokeMut.isPending}
                          onClick={() => {
                            if (confirm(`Revoke session from "${s.clientName}"?`)) {
                              revokeMut.mutate(s.id);
                            }
                          }}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
